import { create } from 'zustand';
import type { EntitlementFeatureKey, GatingMode } from '@/types/package';

/**
 * Gating store (client state only)
 *
 * - `mode`: LIFETIME_GATING_MODE as last seen in GET /api/teams/package (the axios interceptor only opens the
 *   upsell modal in 'enforce', so 'off' / 'shadow' stay exactly as before).
 * - `lockedSlugs`: feature -> upsell plan slug, learned from `locked: true` GET responses.
 * - `upsell`: the ONE global upsell modal (opened by a 403 FEATURE_NOT_AVAILABLE, once per feature per session).
 * - `tokensModalOpen`: the AI-token upsell modal (403 INSUFFICIENT_AI_TOKENS on paths without their own handling).
 */

/** Details of a monthly-quota upsell (403 QUOTA_EXCEEDED) */
export interface QuotaUpsellDetails {
  quotaKey: string;
  used: number | null;
  limit: number | null;
  resetsAt: string | null;
  /** true = a fixed per-brand count limit (not a monthly quota): own card copy */
  fixedLimit?: boolean;
}

export interface UpsellRequest {
  /** Dedup key: a feature key, `quota:<key>` or `platform:<PLATFORM>` */
  feature: string;
  upsellPlanSlug: string | null;
  /** Card variant; omitted = the classic feature card */
  variant?: 'feature' | 'quota' | 'platform';
  quota?: QuotaUpsellDetails;
  platform?: string;
}

interface GatingStore {
  mode: GatingMode;
  lockedSlugs: Partial<Record<string, string | null>>;
  upsell: UpsellRequest | null;
  tokensModalOpen: boolean;
  setMode: (mode: GatingMode) => void;
  recordLocked: (feature: string, upsellPlanSlug: string | null) => void;
  /** `force` skips the once-per-session guard (an explicit click on a locked control) */
  /** Returns true when an upsell is visible afterwards (just opened or already open), false when it was suppressed. */
  openUpsell: (request: UpsellRequest, force?: boolean) => boolean;
  closeUpsell: () => void;
  /** Returns true when the tokens modal is visible afterwards (just opened or already open), false on cooldown. */
  openTokensModal: () => boolean;
  closeTokensModal: () => void;
  /** Team switch / God Mode enter-exit / logout: forget per-team gating memory */
  resetForTeamChange: () => void;
}

/** Features whose upsell modal was already shown in this session (non-intrusive: once each). */
const shownUpsells = new Set<string>();

/** Do not reopen the AI-token modal within this window after the user closed it. */
const TOKENS_MODAL_COOLDOWN_MS = 10 * 60 * 1000;
/** Epoch ms when the user last closed the token modal (reset on team change, so effectively per team). */
let tokensModalClosedAt = 0;

export const useGatingStore = create<GatingStore>((set, get) => ({
  mode: 'off',
  lockedSlugs: {},
  upsell: null,
  tokensModalOpen: false,
  setMode: (mode) => {
    if (get().mode !== mode) set({ mode });
  },
  recordLocked: (feature, upsellPlanSlug) => {
    if (feature in get().lockedSlugs && get().lockedSlugs[feature] === upsellPlanSlug) return;
    // A null slug (unknown plan) never overwrites a slug that was already learned
    if (upsellPlanSlug === null && get().lockedSlugs[feature]) return;
    set((state) => ({ lockedSlugs: { ...state.lockedSlugs, [feature]: upsellPlanSlug } }));
  },
  openUpsell: (request, force = false) => {
    if (get().upsell) return true;
    if (!force && shownUpsells.has(request.feature)) return false;
    shownUpsells.add(request.feature);
    set({ upsell: request });
    return true;
  },
  closeUpsell: () => set({ upsell: null }),
  openTokensModal: () => {
    if (get().tokensModalOpen) return true;
    if (Date.now() - tokensModalClosedAt < TOKENS_MODAL_COOLDOWN_MS) return false;
    set({ tokensModalOpen: true });
    return true;
  },
  closeTokensModal: () => {
    tokensModalClosedAt = Date.now();
    set({ tokensModalOpen: false });
  },
  resetForTeamChange: () => {
    shownUpsells.clear();
    tokensModalClosedAt = 0;
    set({ upsell: null, tokensModalOpen: false, lockedSlugs: {} });
  },
}));

/** Typed marker of a gated GET answered with HTTP 200 `{ locked: true, feature, upsellPlanSlug, data }`. */
export interface LockedResponseBody {
  locked: true;
  feature: EntitlementFeatureKey | string;
  upsellPlanSlug: string | null;
}

export const isLockedResponseBody = (body: unknown): body is LockedResponseBody => {
  if (!body || typeof body !== 'object') return false;
  const candidate = body as { locked?: unknown; feature?: unknown };
  return candidate.locked === true && typeof candidate.feature === 'string';
};
